/**
 * Central SSO Agent client (Web).
 * No OAuth client secrets in the browser — only public start/exchange APIs.
 *
 * Security constraints (fail closed):
 * - Provider id allowlist shape only
 * - redirect_uri locked to same origin / env allowlist
 * - authorization_url must be https (or http loopback for rare local IdP mocks)
 * - exchange codes never written to localStorage; api_key never in sessionStorage
 * - single-flight exchange via in-memory promise map (StrictMode-safe)
 */
import {
  apiFetch,
  API_JSON_ACCEPT,
  isApiMisrouteResponse,
  resolveApiBaseUrl,
} from '../../../utils/apiBase';
import {
  defaultSsoRedirectUri,
  ensureCanonicalSpaHost,
  isBlockedSsoProvider,
  isKnownApiHostname,
  resolveAppEnvironment,
  sameSpaSite,
  ssoAllowLegacyApiKeyClient,
  ssoAgentOauthCallbackPath,
  ssoApiPath,
  ssoAuthCallbackPath,
  ssoAuthMaxAgeMs,
  ssoOauthStateStorageKey,
  ssoPreferCookieSession,
  ssoRequireOauthState,
  ssoWebClientAppId,
} from '../../../config/environments';
import { generatePkcePair, persistPkceForRedirect, takePkceForExchange } from './pkce';
import { markCookieSession, setCsrfToken } from '../../../utils/authTokenStore';

export function getSsoClientAppId() {
  return ssoWebClientAppId();
}

/** Resolved client id (optional override). */
export function resolveSsoClientAppId(clientAppId) {
  return String(clientAppId || ssoWebClientAppId()).slice(0, 64);
}

/** @deprecated Use getSsoClientAppId() */
export const SSO_CLIENT_APP_ID = getSsoClientAppId();

const PROVIDER_ID_RE = /^[a-z][a-z0-9_-]{0,31}$/;
const EXCHANGE_CODE_RE = /^[A-Za-z0-9._~-]{8,512}$/;
const MAX_AUTH_URL_LEN = 4096;

/** In-tab single-flight exchange (avoids storing api_key in sessionStorage). */
const _exchangeInflight = new Map();
const _exchangeDone = new Map();

function scrubInflight(code) {
  try {
    _exchangeInflight.delete(code);
  } catch (_) {}
}

function isApiHost(hostname) {
  return isKnownApiHostname(hostname);
}

function isLoopbackHost(hostname) {
  const host = String(hostname || '').trim().toLowerCase();
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '[::1]' ||
    host.endsWith('.localhost')
  );
}

function currentPageIsLoopback() {
  try {
    return typeof window !== 'undefined' && isLoopbackHost(window.location.hostname);
  } catch (_) {
    return false;
  }
}

function validateProviderPayload(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('provider_unavailable');
  }
  const callbackUrl = String(data.oauth_callback_url || '').trim();
  if (!callbackUrl) return data;
  try {
    const u = new URL(callbackUrl);
    const path = (u.pathname || '/').replace(/\/+$/, '') || '/';
    if (path !== ssoAgentOauthCallbackPath().replace(/\/+$/, '')) {
      throw new Error('provider_misconfigured');
    }
    if (!currentPageIsLoopback() && isLoopbackHost(u.hostname)) {
      throw new Error('provider_misconfigured');
    }
    const apiBase = resolveApiBaseUrl();
    if (apiBase) {
      try {
        const api = new URL(apiBase);
        if (
          !isLoopbackHost(api.hostname) &&
          !isLoopbackHost(u.hostname) &&
          api.hostname !== u.hostname
        ) {
          throw new Error('provider_misconfigured');
        }
      } catch (_) {
        /* ignore invalid API base */
      }
    }
  } catch (err) {
    if (err && err.message === 'provider_misconfigured') throw err;
    throw new Error('provider_unavailable');
  }
  return data;
}

function originAuthCallback() {
  return `${window.location.origin}${ssoAuthCallbackPath()}`;
}

function normalizeAuthCallback(url) {
  try {
    const u = new URL(url);
    if (isApiHost(u.hostname)) return null;
    const cb = ssoAuthCallbackPath();
    // Web must use configured callback path — never bare public /callback
    const path = (u.pathname || '/').replace(/\/+$/, '') || '/';
    if (path === '/callback') {
      u.pathname = cb;
    } else if (path !== cb && path !== '/oauth/callback') {
      u.pathname = cb;
    }
    u.hash = '';
    return u.toString();
  } catch (_) {
    return null;
  }
}

/** SPA callback URL that Agent may redirect to after IdP login. */
export function getSsoWebRedirectUri() {
  if (typeof window === 'undefined') return '';

  const baked =
    (process.env.REACT_APP_SSO_REDIRECT_URI || '').trim() || defaultSsoRedirectUri();
  const env = resolveAppEnvironment();

  // ***** Code Start BH15092026_1445 - Production pins candorsys; never fall back to cantest
  // Never start OAuth from a page hosted on the LLM API host — fail closed if misconfigured
  try {
    if (isApiHost(window.location.hostname)) {
      const safe = normalizeAuthCallback(baked);
      if (!safe) {
        console.error(
          '[SSO] page is on API host and no safe SPA redirect is configured (REACT_APP_SSO_REDIRECT_URI / host matrix)'
        );
        return '';
      }
      console.warn('[SSO] page is on API host; using configured SPA callback', safe);
      return safe;
    }
  } catch (_) {}

  if (env === 'production') {
    const pinned = normalizeAuthCallback(baked);
    if (!pinned) {
      console.error('[SSO] production package missing SPA redirect_uri');
      return '';
    }
    try {
      const u = new URL(pinned);
      if (u.protocol !== 'https:') {
        console.error('[SSO] production redirect_uri must be https');
        return '';
      }
      if (isApiHost(u.hostname)) {
        console.error('[SSO] production redirect_uri must not be an API host');
        return '';
      }
    } catch (_) {
      return '';
    }
    // Pin to packaged Prod callback even if the user opened a Dev host (cantest).
    // Caller should bounce via ensureCanonicalSpaHost() before start.
    if (!sameSpaSite(pinned, window.location.origin)) {
      console.warn(
        '[SSO] production redirect_uri pinned to packaged SPA (ignoring non-canonical page origin)',
        pinned
      );
    }
    return pinned;
  }
  // ***** Code End BH15092026_1445

  if (baked) {
    try {
      const u = new URL(baked);
      if (u.origin !== window.location.origin) {
        console.warn(
          '[SSO] REACT_APP_SSO_REDIRECT_URI origin mismatch; using window.origin callback'
        );
        return originAuthCallback();
      }
      if (u.protocol !== 'https:' && u.protocol !== 'http:') {
        return originAuthCallback();
      }
      if (window.location.protocol === 'https:' && u.protocol !== 'https:') {
        return originAuthCallback();
      }
      return normalizeAuthCallback(u.toString()) || originAuthCallback();
    } catch (_) {
      return originAuthCallback();
    }
  }
  return originAuthCallback();
}

export function sanitizeProviderId(provider) {
  const id = String(provider || '')
    .trim()
    .toLowerCase();
  if (!PROVIDER_ID_RE.test(id) || id === 'password' || isBlockedSsoProvider(id)) {
    return null;
  }
  return id;
}

/**
 * Reject javascript:/data:/open redirects. HTTPS preferred; localhost http only in non-prod.
 */
export function isSafeAuthorizationUrl(url) {
  if (!url || typeof url !== 'string' || url.length > MAX_AUTH_URL_LEN) return false;
  let u;
  try {
    u = new URL(url);
  } catch (_) {
    return false;
  }
  if (u.username || u.password) return false;
  if (u.protocol === 'https:') return true;
  if (u.protocol === 'http:') {
    const host = (u.hostname || '').toLowerCase();
    const local =
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '[::1]' ||
      host.endsWith('.localhost');
    // Never allow plain http IdP in production builds
    if (process.env.NODE_ENV === 'production' && !local) return false;
    return local;
  }
  return false;
}

function extractErrorDetail(data, fallback) {
  if (!data || typeof data !== 'object') return fallback;
  const d = data.detail;
  if (typeof d === 'string' && d.length < 200) return d;
  if (typeof data.message === 'string' && data.message.length < 200) return data.message;
  if (typeof data.error === 'string' && data.error.length < 80) return data.error;
  return fallback;
}

function parseWebAuthSuccess(data) {
  const email =
    (typeof data.email === 'string' && data.email) ||
    (data.user && typeof data.user.email === 'string' && data.user.email) ||
    '';
  const user = data.user && typeof data.user === 'object' ? data.user : { email };
  const provider =
    typeof data.provider === 'string'
      ? sanitizeProviderId(data.provider) || data.provider
      : undefined;
  const cookieSession =
    data.session_channel === 'cookie' || data.token_type === 'cookie';
  if (typeof data.csrf_token === 'string' && data.csrf_token) {
    setCsrfToken(data.csrf_token);
  }
  if (cookieSession) {
    return {
      cookieSession: true,
      apiKey: '',
      user,
      email,
      provider,
      license: data.license,
      csrf_token: typeof data.csrf_token === 'string' ? data.csrf_token : undefined,
    };
  }
  // ***** Code Start BH17082026_1055 - QA/Prod cookie mode never accepts body api_key
  if (ssoPreferCookieSession() || !ssoAllowLegacyApiKeyClient()) {
    if (typeof data.csrf_token === 'string' && data.csrf_token) {
      return {
        cookieSession: true,
        apiKey: '',
        user,
        email,
        provider,
        license: data.license,
        csrf_token: data.csrf_token,
      };
    }
    throw new Error('account_error');
  }
  // ***** Code End BH17082026_1055
  const apiKey = typeof data.api_key === 'string' ? data.api_key.trim() : '';
  if (!apiKey || apiKey.length < 16 || apiKey.length > 512) {
    throw new Error('account_error');
  }
  if (!/^[A-Za-z0-9._~+-]+$/.test(apiKey)) {
    throw new Error('account_error');
  }
  markCookieSession(false);
  return {
    cookieSession: false,
    apiKey,
    user,
    email,
    provider,
    license: data.license,
  };
}

export async function listSsoProviders() {
  // ***** Code Start BH28082026_1115 - Misroute-tolerant providers fetch
  const res = await apiFetch(ssoApiPath('providers'), {
    method: 'GET',
    headers: { Accept: API_JSON_ACCEPT },
    credentials: 'include',
    cache: 'no-store',
  });
  let raw = '';
  try {
    raw = await res.clone().text();
  } catch (_) {
    raw = '';
  }
  if (isApiMisrouteResponse(res, raw)) {
    throw new Error('api_unreachable');
  }
  if (!res.ok) {
    throw new Error(`SSO providers failed: HTTP ${res.status}`);
  }
  let data;
  try {
    data = raw ? JSON.parse(raw) : await res.json();
  } catch (_) {
    throw new Error('SSO providers returned invalid payload');
  }
  if (!data || typeof data !== 'object') {
    throw new Error('SSO providers returned invalid payload');
  }
  validateProviderPayload(data);
  if (Array.isArray(data.providers)) {
    data.providers = data.providers.filter(
      (p) => p && sanitizeProviderId(p.id || p.provider)
    );
  }
  return data;
  // ***** Code End BH28082026_1115
}

export async function startSsoOAuth(provider, clientAppId) {
  const pid = sanitizeProviderId(provider);
  if (!pid) {
    throw new Error('invalid_request');
  }
  const appId = resolveSsoClientAppId(clientAppId);
  if (!/^[a-z][a-z0-9_]{1,63}$/.test(appId)) {
    throw new Error('invalid_request');
  }
  const redirectUri = getSsoWebRedirectUri();
  if (!redirectUri) {
    throw new Error('provider_misconfigured');
  }

  const pkce = await generatePkcePair();
  persistPkceForRedirect(pkce.verifier, redirectUri);

  // ***** Code Start BH28082026_1115 - Misroute-tolerant OAuth start
  const res = await apiFetch(ssoApiPath('login_start'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: API_JSON_ACCEPT,
    },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({
      provider: pid,
      client_app_id: appId,
      redirect_uri: redirectUri,
      code_challenge: pkce.challenge,
      code_challenge_method: 'S256',
    }),
  });
  let data = {};
  let raw = '';
  try {
    raw = await res.clone().text();
    data = raw ? JSON.parse(raw) : {};
  } catch (_) {
    data = {};
  }
  if (isApiMisrouteResponse(res, raw)) {
    throw new Error('api_unreachable');
  }
  if (!res.ok) {
    throw new Error(extractErrorDetail(data, `SSO start failed (${res.status})`));
  }
  // ***** Code End BH28082026_1115
  const authorizationUrl = data.authorization_url;
  if (!isSafeAuthorizationUrl(authorizationUrl)) {
    throw new Error('provider_unavailable');
  }
  return {
    provider: pid,
    authorization_url: authorizationUrl,
    state: typeof data.state === 'string' ? data.state.slice(0, 256) : undefined,
    expires_in: data.expires_in,
  };
}

export async function exchangeSsoCode(code, clientAppId) {
  const raw = String(code || '').trim();
  if (!EXCHANGE_CODE_RE.test(raw)) {
    throw new Error('invalid_request');
  }
  const appId = resolveSsoClientAppId(clientAppId);
  const pkce = takePkceForExchange();
  const redirectUri = pkce.redirectUri || getSsoWebRedirectUri();

  const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ac ? setTimeout(() => ac.abort(), 20_000) : null;
  let res;
  try {
    // ***** Code Start BH28082026_1115 - Misroute-tolerant token exchange
    res = await apiFetch(ssoApiPath('token_exchange'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: API_JSON_ACCEPT,
      },
      credentials: 'include',
      cache: 'no-store',
      signal: ac ? ac.signal : undefined,
      body: JSON.stringify({
        code: raw,
        client_app_id: appId,
        code_verifier: pkce.verifier || undefined,
        redirect_uri: redirectUri || undefined,
      }),
    });
  } catch (err) {
    const aborted =
      (err && err.name === 'AbortError') ||
      /aborted|timeout/i.test(String((err && err.message) || ''));
    throw new Error(aborted ? 'invalid_session' : 'provider_unavailable');
  } finally {
    if (timer) clearTimeout(timer);
  }
  let data = {};
  let bodyText = '';
  try {
    bodyText = await res.clone().text();
    data = bodyText ? JSON.parse(bodyText) : {};
  } catch (_) {
    data = {};
  }
  if (isApiMisrouteResponse(res, bodyText)) {
    throw new Error('api_unreachable');
  }
  if (!res.ok) {
    throw new Error(extractErrorDetail(data, `Token exchange failed (${res.status})`));
  }
  // ***** Code End BH28082026_1115
  return parseWebAuthSuccess(data);
}

export async function ssoPasswordLogin(email, password, clientAppId) {
  const em = String(email || '').trim().toLowerCase().slice(0, 254);
  if (!em || !password || String(password).length > 256) {
    throw new Error('invalid_request');
  }
  // ***** Code Start BH28082026_1115 - Misroute-tolerant password login
  const res = await apiFetch(ssoApiPath('login_password'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: API_JSON_ACCEPT,
    },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({
      email: em,
      password: String(password),
      client_app_id: resolveSsoClientAppId(clientAppId),
    }),
  });
  let data = {};
  let bodyText = '';
  try {
    bodyText = await res.clone().text();
    data = bodyText ? JSON.parse(bodyText) : {};
  } catch (_) {
    data = {};
  }
  if (isApiMisrouteResponse(res, bodyText)) {
    throw new Error('api_unreachable');
  }
  // ***** Code End BH28082026_1115
  if (!res.ok) {
    const err = new Error(extractErrorDetail(data, 'Login failed'));
    err.status = res.status;
    throw err;
  }
  return parseWebAuthSuccess(data);
}

/**
 * Start OAuth: Agent start → full browser navigate to IdP.
 * Optional mode stored for UX messages only (no secrets).
 */
export async function beginSsoBrowserLogin(providerId, mode = 'login') {
  const pid = sanitizeProviderId(providerId);
  if (!pid) {
    throw new Error('invalid_request');
  }
  // ***** Code Start BH15092026_1445 - Bounce production package off cantest before OAuth
  if (ensureCanonicalSpaHost()) {
    return { redirected: true, provider: pid };
  }
  // ***** Code End BH15092026_1445
  const safeMode = mode === 'register' ? 'register' : 'login';
  try {
    sessionStorage.setItem('sso_auth_mode', safeMode);
    sessionStorage.setItem('sso_auth_provider', pid);
    sessionStorage.setItem('sso_auth_started_at', String(Date.now()));
    // Never store codes or keys in sessionStorage up-front
    sessionStorage.removeItem('sso_code_result');
  } catch (_) {}

  const start = await startSsoOAuth(pid);
  if (!isSafeAuthorizationUrl(start.authorization_url)) {
    throw new Error('provider_unavailable');
  }
  // ***** Code Start BH17082026_1035 - Persist Agent OAuth state (config-driven key)
  try {
    const stateKey = ssoOauthStateStorageKey();
    if (start.state) {
      sessionStorage.setItem(stateKey, String(start.state).slice(0, 256));
    } else {
      sessionStorage.removeItem(stateKey);
    }
  } catch (_) {}
  // ***** Code End BH17082026_1035
  // Full navigation — leave after validation
  window.location.assign(start.authorization_url);
  return start;
}

/** Strip exchange code from address bar to reduce history/referrer leakage. */
export function scrubSsoCallbackUrl() {
  if (typeof window === 'undefined' || !window.history || !window.history.replaceState) return;
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.has('code') || u.searchParams.has('error') || u.searchParams.has('state')) {
      u.search = '';
      u.hash = u.hash && u.hash.includes('?') ? u.hash.split('?')[0] : u.hash;
      window.history.replaceState({}, document.title, u.pathname + (u.hash || ''));
    }
  } catch (_) {}
}

/** Complete Agent redirect: /auth/callback?code=...&state=... or ?error=... */
export async function completeSsoBrowserCallback(searchParams) {
  const error = searchParams.get('error') || searchParams.get('error_description');
  if (error) {
    // Map to stable short code; never surface raw stack/PII dumps
    const stable = String(error).slice(0, 80);
    throw new Error(stable);
  }
  const code = String(searchParams.get('code') || '').trim();
  if (!EXCHANGE_CODE_RE.test(code)) {
    throw new Error('invalid_request');
  }

  // ***** Code Start BH17082026_1035 - Config-driven OAuth state binding
  const requireState = ssoRequireOauthState();
  const stateKey = ssoOauthStateStorageKey();
  let expectedState = '';
  try {
    expectedState = sessionStorage.getItem(stateKey) || '';
  } catch (_) {
    expectedState = '';
  }
  const returnedState = String(searchParams.get('state') || '').trim().slice(0, 256);
  if (requireState) {
    if (!expectedState || !returnedState || returnedState !== expectedState) {
      try {
        sessionStorage.removeItem(stateKey);
      } catch (_) {}
      throw new Error('invalid_session');
    }
  } else if (expectedState && returnedState && returnedState !== expectedState) {
    try {
      sessionStorage.removeItem(stateKey);
    } catch (_) {}
    throw new Error('invalid_session');
  }
  try {
    sessionStorage.removeItem(stateKey);
  } catch (_) {}
  // ***** Code End BH17082026_1035

  // Stale start guard (matrix / env max age — no hardcoded minutes in callers)
  try {
    const started = Number(sessionStorage.getItem('sso_auth_started_at') || 0);
    const maxAge = ssoAuthMaxAgeMs();
    if (started && Date.now() - started > maxAge) {
      throw new Error('invalid_session');
    }
  } catch (e) {
    if (e && e.message === 'invalid_session') throw e;
  }

  // In-memory: return completed exchange for StrictMode remount
  const done = _exchangeDone.get(code);
  if (done && (done.apiKey || done.cookieSession)) {
    return done;
  }

  // Single-flight for same code
  if (_exchangeInflight.has(code)) {
    return _exchangeInflight.get(code);
  }

  const work = (async () => {
    try {
      const result = await exchangeSsoCode(code);
      _exchangeDone.set(code, result);
      // Bound memory: drop oldest entries
      if (_exchangeDone.size > 8) {
        const first = _exchangeDone.keys().next().value;
        _exchangeDone.delete(first);
      }
      try {
        sessionStorage.removeItem('sso_auth_mode');
        sessionStorage.removeItem('sso_auth_provider');
        sessionStorage.removeItem('sso_auth_started_at');
        // Purge any legacy sessionStorage result caches from older builds
        for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
          const k = sessionStorage.key(i);
          if (k && (k.startsWith('sso_code_') || k.startsWith('sso_code_result_'))) {
            sessionStorage.removeItem(k);
          }
        }
      } catch (_) {}
      scrubSsoCallbackUrl();
      return result;
    } finally {
      scrubInflight(code);
    }
  })();

  _exchangeInflight.set(code, work);
  return work;
}

/** Test helper — clear in-memory exchange maps */
export function __resetSsoClientMemoryForTests() {
  _exchangeInflight.clear();
  _exchangeDone.clear();
}
